import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import CarouselDots from '../components/CarouselDots';
import {
  Building2,
  CircleDollarSign,
  FileCheck,
  Landmark,
  ShieldPlus,
  Trophy,
  Zap,
} from 'lucide-react';
import '../styles/Home.css';

function Home() {
  const navigate = useNavigate();

  // Stashed until we have real, approved client testimonials — do not show fabricated quotes in prod.
  // Set to true to restore the carousel (logic below is unchanged).
  const SHOW_TESTIMONIALS = false;
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [direction, setDirection] = useState('forward');

  // Empty until real approved quotes are added. Shape: { text, author, practice }
  const testimonials = [];

  const nextTestimonial = () => {
    setDirection('forward');
    const next = currentTestimonial + 1 >= testimonials.length ? 0 : currentTestimonial + 1;
    setCurrentTestimonial(next);
  };

  const prevTestimonial = () => {
    setDirection('backward');
    const prev = currentTestimonial - 1 < 0 ? testimonials.length - 1 : currentTestimonial - 1;
    setCurrentTestimonial(prev);
  };

  const handleDotClick = (index) => {
    // Determine direction for testimonial animation
    const diff = index - currentTestimonial;
    const wrapDiff = index < currentTestimonial 
      ? (testimonials.length + diff) 
      : diff;
    
    // If wrapping around, use shorter path
    if (wrapDiff <= testimonials.length / 2) {
      setDirection('forward');
    } else {
      setDirection('backward');
    }
    
    setCurrentTestimonial(index);
  };

  return (
    <div className="home">
      <section className="hero">
        <div className="hero-content">
          <h1>Jones & Booth Law</h1>
          <p>Jones & Booth provides comprehensive legal services with decades of experience across personal injury, estate, and business matters. Our team is committed to protecting your rights and achieving the best possible outcomes for your case.</p>
          <div className="hero-buttons">
            <Button variant="primary" onClick={() => navigate('/qualify')}>
              Contact Us Today
            </Button>
            <Button variant="secondary" onClick={() => navigate('/about')}>
              Who We Are
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-us">
        <div className="why-choose-content">
          <h2>Why Choose Our Law Firm?</h2>
          <div className="why-choose-grid">
            <div className="why-item">
              <div className="why-icon" aria-hidden="true">
                <Trophy size={48} strokeWidth={1.75} />
              </div>
              <h3>20+ Years Experience</h3>
              <p>Decades of proven results with thousands of successful cases and satisfied clients.</p>
            </div>
            <div className="why-item">
              <div className="why-icon" aria-hidden="true">
                <CircleDollarSign size={48} strokeWidth={1.75} />
              </div>
              <h3>Fighting for Full Value</h3>
              <p>We fight for the best adjustment, settlement, or result possible so your recovery is protected and maximized.</p>
            </div>
            <div className="why-item">
              <div className="why-icon" aria-hidden="true">
                <Zap size={48} strokeWidth={1.75} />
              </div>
              <h3>Fast Results</h3>
              <p>Quick case evaluation and immediate action. We understand time is critical in legal matters.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Practice Areas Section */}
      <section className="practice-areas">
        <div className="practice-content">
          <h2>How We Can Help You</h2>
          <div className="practice-grid">
            <div className="practice-item">
              <div className="practice-icon" aria-hidden="true">
                <ShieldPlus size={72} strokeWidth={1.75} />
              </div>
              <h3>Personal Injury</h3>
              <p>Car accidents, slip & falls, workplace injuries, catastrophic injury, and wrongful death. We fight for the compensation you deserve.</p>
              <Button variant="secondary" onClick={() => navigate('/qualify')}>
                Get Free Case Review
              </Button>
            </div>
            <div className="practice-item">
              <div className="practice-icon" aria-hidden="true">
                <FileCheck size={72} strokeWidth={1.75} />
              </div>
              <h3>Estate Law</h3>
              <p>Wills, trusts, and estate planning. Protect your legacy and ensure your wishes are honored.</p>
              <Button variant="secondary" onClick={() => navigate('/qualify')}>
                Schedule Consultation
              </Button>
            </div>
            <div className="practice-item">
              <div className="practice-icon" aria-hidden="true">
                <Landmark size={72} strokeWidth={1.75} />
              </div>
              <h3>Probate</h3>
              <p>Estate administration and court proceedings. Compassionate guidance settling a loved one's estate.</p>
              <Button variant="secondary" onClick={() => navigate('/qualify')}>
                Schedule Consultation
              </Button>
            </div>
            <div className="practice-item">
              <div className="practice-icon" aria-hidden="true">
                <Building2 size={72} strokeWidth={1.75} />
              </div>
              <h3>Real Estate Law</h3>
              <p>Commercial and residential lease disputes, quiet title, and partition actions.</p>
              <Button variant="secondary" onClick={() => navigate('/qualify')}>
                Schedule Consultation
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonials Carousel — stashed (SHOW_TESTIMONIALS) until real quotes are available */}
      {SHOW_TESTIMONIALS && testimonials.length > 0 && (
        <section className="testimonials">
          <div className="testimonials-content">
            <h2>What Our Clients Say</h2>
            <div className="testimonial-carousel">
              <button className="carousel-btn prev" onClick={prevTestimonial}>
                ‹
              </button>
              <div className="testimonials-grid">
                <div className={`testimonial-item direction-${direction}`} key={currentTestimonial}>
                  <div className="testimonial-text">
                    "{testimonials[currentTestimonial].text}"
                  </div>
                  <div className="testimonial-author">
                    <strong>{testimonials[currentTestimonial].author}</strong>
                    <span>{testimonials[currentTestimonial].practice}</span>
                  </div>
                </div>
              </div>
              <button className="carousel-btn next" onClick={nextTestimonial}>
                ›
              </button>
            </div>
            <CarouselDots
              totalItems={testimonials.length}
              currentIndex={currentTestimonial}
              onDotClick={handleDotClick}
              visibleCount={5}
            />
          </div>
        </section>
      )}

      {/* Contact CTA Section */}
      <section className="contact-cta">
        <div className="contact-cta-content">
          <h2>Ready to Get Started?</h2>
          <p>Don't wait - legal issues only get more complex over time. Get your free consultation today and let us fight for your rights.</p>
          <div className="cta-buttons">
            <Button variant="primary" onClick={() => navigate('/qualify')}>
              Get Free Consultation
            </Button>
            <Button variant="secondary" onClick={() => navigate('/about')}>
              Meet Our Team
            </Button>
            <Button variant="secondary" onClick={() => navigate('/blog')}>
              Read Our Blog
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
